import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { camelCase, isEmpty, mapKeys, mapValues, map, get } from 'lodash';
import { useQuery, useSession } from 'common/hooks';
import { UseQueryOptions } from 'common/types/Api.type';
import { ActiveFilters, Filter } from '../types/filters.type';
import { mapSearchString } from '../search.utils';
import { fetchFilterMetadataApi } from '../search.service';

// Resolve filter names for the current URL, including navigation within Explore.
export const useFetchFilterMetadata = ({ onSuccess, ...options }: UseQueryOptions = {}) => {
  const { account: user } = useSession();
  const { search } = useLocation();

  const queryResponse = useQuery(
    [user?.organizationId, 'filter-metadata', search],
    () => fetchFilterMetadataApi({ searchString: search }),
    {
      select: useCallback(
        ({ statusCode, data }: any) => {
          const filters = mapSearchString(search);
          const excludeChildrenIds: string[] = get(filters, 'excludeChildren') || [];
          const excludeChildren: Filter[] = [];

          const filtersWithoutExcludedChildren: ActiveFilters = mapKeys(
            data.result,
            (value, key) => {
              return camelCase(key);
            },
          );

          const activeFilters = mapValues(filtersWithoutExcludedChildren, (value) => {
            if (!isEmpty(excludeChildrenIds)) {
              return map(value, (filter) => {
                const excludeChildrenIndex = excludeChildrenIds.indexOf(filter.id);
                if (excludeChildrenIndex > -1) {
                  excludeChildrenIds.splice(excludeChildrenIndex, 1);
                  const excludeChildrenFilter = {
                    ...filter,
                    name: `${filter.name} (unassigned)`,
                    excludeChildren: true,
                  };
                  excludeChildren.push(excludeChildrenFilter);
                  return excludeChildrenFilter;
                }
                return filter;
              });
            }

            return value;
          });

          if (!isEmpty(excludeChildren)) {
            activeFilters.excludeChildren = excludeChildren;
          }

          return {
            statusCode,
            data: {
              result: activeFilters,
            },
          };
        },
        [search],
      ),
      enabled: !!search,
      onSuccess: (data) => {
        onSuccess && onSuccess(data);
      },
      ...options,
    },
  );

  return {
    ...queryResponse,
    data: queryResponse.data?.data,
  };
};
